import React, { useState, useEffect } from 'react';
import { X, User, Mail, Lock, Building2, Shield, Users } from 'lucide-react';
import { platformAdminService } from '../../../services/platformAdminService';

export default function CreateUserModal({ onClose, onCreate, companies = [] }) {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    role: 'company_user',
    companyId: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [adminStatus, setAdminStatus] = useState(null);

  // Check admin status on component mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      const status = await platformAdminService?.checkPlatformAdminStatus();
      setAdminStatus(status);
      
      if (!status?.isAdmin && !status?.error) {
        setErrors({
          permission: `Access denied. Current role: ${status?.role || 'unknown'}. Only platform administrators can create users.`
        });
      }
    };

    checkAdminStatus();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.companyId) {
      newErrors.companyId = 'Company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!adminStatus?.isAdmin) {
      setErrors({ 
        permission: 'You must be a platform administrator to create users. Please contact your system administrator.' 
      });
      return;
    }
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      const result = await platformAdminService?.createUserForCompany({
        email: formData?.email,
        fullName: formData?.fullName,
        password: formData?.password,
        role: formData?.role,
        companyId: formData?.companyId
      });

      if (result?.error) {
        setErrors({ submit: result?.error });
      } else {
        onCreate?.(result?.data);
        onClose?.();
      }
    } catch (error) {
      setErrors({ submit: 'Unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'company_admin':
        return Shield;
      case 'company_user':
        return Users;
      default:
        return User;
    }
  };

  const selectedCompany = companies?.find(c => c?.id === formData?.companyId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New User</h2>
              <p className="text-sm text-gray-500">Add a new user to a company</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Admin Status Check */}
        {adminStatus && !adminStatus?.isAdmin && (
          <div className="p-6 bg-red-50 border-l-4 border-red-400">
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Permission Required</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Only platform administrators can create users.</p>
                  <p className="mt-1">
                    <strong>Your role:</strong> {adminStatus?.role || 'Unknown'}
                  </p>
                  {adminStatus?.error && (
                    <p className="mt-1 text-red-600">Error: {adminStatus?.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form - only show if admin */}
        {adminStatus?.isAdmin && (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <div className="relative">
                <Building2 className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={formData?.companyId}
                  onChange={(e) => handleInputChange('companyId', e?.target?.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.companyId ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a company</option>
                  {companies?.map((company) => (
                    <option key={company?.id} value={company?.id}>
                      {company?.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors?.companyId && (
                <p className="mt-1 text-sm text-red-600">{errors?.companyId}</p>
              )}
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.fullName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter user's full name"
                />
              </div>
              {errors?.fullName && (
                <p className="mt-1 text-sm text-red-600">{errors?.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
              </div>
              {errors?.email && (
                <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <div className="space-y-3">
                {[
                  { value: 'company_user', label: 'Company User', description: 'Basic user with limited permissions' },
                  { value: 'company_admin', label: 'Company Admin', description: 'Admin user with full company permissions' }
                ]?.map((role) => {
                  const IconComponent = getRoleIcon(role?.value);
                  return (
                    <label key={role?.value} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        value={role?.value}
                        checked={formData?.role === role?.value}
                        onChange={(e) => handleInputChange('role', e?.target?.value)}
                        className="mt-1 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                          <IconComponent className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{role?.label}</div>
                          <div className="text-sm text-gray-500">{role?.description}</div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter password"
                />
              </div>
              {errors?.password && (
                <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Confirm password"
                />
              </div>
              {errors?.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors?.confirmPassword}</p>
              )}
            </div>

            {/* Selected Company Info */}
            {selectedCompany && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    User will be added to: {selectedCompany?.name}
                  </span>
                </div>
              </div>
            )}

            {/* Permission Error */}
            {errors?.permission && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">{errors?.permission}</p>
              </div>
            )}

            {/* Submit Error */}
            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{errors?.submit}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !adminStatus?.isAdmin}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <span>Create User</span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}